/**
 * Lenient normalization for user-entered source links. A contributor who types a bare
 * domain ("www.somesite.com" or "somesite.com/menu") or omits the scheme should NOT be
 * blocked — we prepend `https://` and validate. Returns the normalized absolute URL, or
 * null when the input is empty or can't be parsed as an http(s) URL with a dotted host.
 *
 * Shared by every submit form (client) and the submissions API (server), so the same
 * leniency applies regardless of where the value comes from.
 */
export function normalizeUrl(raw: string | null | undefined): string | null {
  // Strip whitespace anywhere in the string, not just the ends: mobile keyboards and
  // autocorrect routinely inject a stray space ("google. com", "www. site.com"), and a
  // real web URL never contains a literal space. Without this, such input fails to parse
  // and the whole submission is rejected.
  let trimmed = (raw ?? "").replace(/\s+/g, "");
  // Pasted links often arrive wrapped or trailed by punctuation from the surrounding
  // sentence: <https://x.com>, "x.com", (x.com), x.com, — none of it belongs to the URL.
  trimmed = trimmed.replace(/^[<"'“‘(\[]+/, "").replace(/[>"'”’)\],;]+$/, "");
  // Repair common scheme typos instead of rejecting them: "https//x.com",
  // "https:/x.com", "htps://x.com", "http:\\x.com". Without this the fallback below
  // prepends https:// and the typo'd scheme becomes a dotless hostname → rejected.
  trimmed = trimmed.replace(/^h?t{1,2}ps?:?[/\\]{1,3}/i, (m) =>
    /s:?[/\\]*$/i.test(m) ? "https://" : "http://",
  );
  if (!trimmed) return null;

  // Reject dangerous / non-web schemes outright — a stored source_url is later rendered
  // as a clickable link, so never let javascript:/data:/etc. through.
  if (/^(javascript|data|vbscript|file|mailto|tel|blob):/i.test(trimmed)) return null;

  // Keep an explicit http(s) scheme; otherwise assume https (handles bare domains).
  const hasWebScheme = /^https?:\/\//i.test(trimmed);
  const candidate = hasWebScheme ? trimmed : `https://${trimmed.replace(/^\/+/, "")}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // A real web source has a dotted hostname — rejects "localhost", typos like "foo", etc.
  if (!url.hostname.includes(".")) return null;
  // WHATWG URL accepts commas etc. in hostnames, and whitespace-stripping can glue a
  // street address onto a domain ("1102 A St, Tacoma, WA example.com" →
  // "1102ast,tacoma,waexample.com"). A real host is letters/digits/hyphens/dots only
  // (IDNs arrive here already punycoded).
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.?$/i.test(url.hostname)) return null;
  return url.toString();
}
